"use client";

import { useAuthFlow } from "../../auth-flow-context";
import { AuthCredentialKind, AuthMethodId, AuthSurface } from "../../types";
import posthog from "posthog-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { signUpLocationId } from "@/components/auth/sign-up/sign-up-source";
import { usePatientClaim, useUpdateClaimedPatient } from "@/hooks/patient-claim/use-patient-claim";
import { useClaimPatient, useCreateAndClaimPatient, useSendMfaCode, useSignInToPortalAccountWithPhone, useVerifyMfaCode } from "@/hooks/patient-claim/use-patient-claim-mutations";
import { toast } from "@/lib/use-toast";
import { PatientAuthType } from "@/types/enums/patient-auth-type";
import { PatientSource } from "@/types/enums/patient-source";
import { PatientClaimAuthMode } from "@/types/patient-claim";
import { SelfSchedulePatient } from "@/types/self-schedule";
import { PhoneAuthActions, PhoneAuthContextValue, PhoneAuthState, PhoneAuthStep } from "./phone-auth-context";
import { PhoneCodeExpiredError, PortalLoginRequiredError, updatePatient } from "@/api/auth/patient-claim";
const hasFullName = (patient: SelfSchedulePatient) => !!patient?.firstName?.trim() && !!patient?.lastName?.trim();
export function usePhoneAuthProvider(): PhoneAuthContextValue {
  const flow = useAuthFlow();
  const {
    claimedPatient,
    candidates: sessionCandidates,
    isPending: isCandidatesPending,
    isResolving,
    isResolvingClaim,
    authMode
  } = usePatientClaim();
  const sendMutation = useSendMfaCode();
  const verifyMutation = useVerifyMfaCode();
  const claimMutation = useClaimPatient();
  const createAndClaimMutation = useCreateAndClaimPatient();
  const portalSignInMutation = useSignInToPortalAccountWithPhone();
  const updateClaimedPatient = useUpdateClaimedPatient();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [enteredCode, setEnteredCode] = useState<string>("");
  const [selectedPatient, setSelectedPatient] = useState<SelfSchedulePatient | null>(null);
  const [portalRequiredPatient, setPortalRequiredPatient] = useState<SelfSchedulePatient | null>(null);
  const [forceNewPatient, setForceNewPatient] = useState<boolean>(false);
  // Set while (and after) the phone session is swapped for the picked
  // patient's portal account. That swap is a completed phone sign-in, so the
  // portal-mode guard below must not hold it back as a handoff.
  const portalSignInViaPhoneRef = useRef(false);

  // Candidates: prefer fresh verify response; fall back to session query.
  // The phone-auth flow only ever produces phone-mode candidate responses;
  // a portal-mode response would mean the token didn't carry a phone_number
  // and shouldn't reach this hook.
  const freshPhoneCandidates = verifyMutation.data?.authMode === PatientClaimAuthMode.PHONE ? verifyMutation.data.candidates : null;
  const candidates = useMemo<SelfSchedulePatient[]>(() => {
    if (freshPhoneCandidates && freshPhoneCandidates.length > 0) return freshPhoneCandidates;
    return sessionCandidates ?? [];
  }, [freshPhoneCandidates, sessionCandidates]);
  const isNewPatient = forceNewPatient || !!freshPhoneCandidates && freshPhoneCandidates.length === 0;
  const isPortalAccountMode = authMode === PatientClaimAuthMode.PORTAL;
  const step: PhoneAuthStep = useMemo(() => {
    if (portalRequiredPatient) return PhoneAuthStep.PORTAL_REQUIRED;
    // Only go to NAME for a candidate that still needs one (anonymous
    // patient row). A picked patient who already has a full name stays
    // on PICK_PATIENT with the "Linking…" state until the claim resolves.
    if (selectedPatient && !hasFullName(selectedPatient)) return PhoneAuthStep.NAME;
    if (!phoneNumber) return PhoneAuthStep.ENTER_PHONE;
    if (!enteredCode) return PhoneAuthStep.ENTER_CODE;
    if (isNewPatient) return PhoneAuthStep.NAME;
    return PhoneAuthStep.PICK_PATIENT;
  }, [phoneNumber, enteredCode, selectedPatient, isNewPatient, portalRequiredPatient]);

  // When the claim resolves a patient (mutation success or rehydrated query),
  // bubble it to the parent AuthFlow as a phone auth success.
  useEffect(() => {
    if (!claimedPatient || createAndClaimMutation.isPending) return;
    // If a phone-code attempt resolved to an existing portal account, keep
    // the user on the portal handoff screen instead of treating the phone
    // credential as a completed patient sign-in. The nested portal AuthFlow
    // owns success once they continue with email/password or Google.
    if (phoneNumber && isPortalAccountMode && !flow.meta.allowPortalAccountHolders && !portalSignInViaPhoneRef.current) {
      return;
    }
    void flow.meta.onSuccess({
      kind: AuthCredentialKind.PHONE,
      patient: claimedPatient,
      // Only this flow's mutation can mark a patient new, not cached chart data.
      patientType: createAndClaimMutation.data?.id === claimedPatient.id ? createAndClaimMutation.data.patientType : PatientAuthType.EXISTING
    });
  }, [claimedPatient, flow.meta, isPortalAccountMode, phoneNumber, createAndClaimMutation.isPending, createAndClaimMutation.data]);
  const actions: PhoneAuthActions = useMemo(() => ({
    submitPhone: async (phone: string) => {
      createAndClaimMutation.reset();
      setPortalRequiredPatient(null);
      const attempt = flow.actions.startAttempt(AuthMethodId.PHONE, "phone_code_send");
      const ok = await flow.actions.runAuth(AuthMethodId.PHONE, () => sendMutation.mutateAsync(phone), attempt);
      if (ok !== null) {
        attempt?.finish("code_sent");
        setPhoneNumber(phone);
      }
    },
    submitCode: async (code: string) => {
      setPortalRequiredPatient(null);
      const attempt_0 = flow.actions.startAttempt(AuthMethodId.PHONE, "phone_code_verify");
      const response = await flow.actions.runAuth(AuthMethodId.PHONE, () => verifyMutation.mutateAsync(code), attempt_0);
      if (response === null) attempt_0?.finish("backend_error");
      if (response) {
        attempt_0?.finish(response.authMode === PatientClaimAuthMode.PORTAL && !flow.meta.allowPortalAccountHolders ? "portal_handoff" : response.authMode === PatientClaimAuthMode.NONE ? "signup_required" : "verified_pending_claim");
        setEnteredCode(code);
        if (response.authMode === PatientClaimAuthMode.PORTAL && !flow.meta.allowPortalAccountHolders) {
          setPortalRequiredPatient(response.patient);
          posthog.capture("patient_portal_login_required", {
            surface: flow.meta.surface,
            source: "phone_verify"
          });
        }
      }
    },
    selectPatient: async (p: SelfSchedulePatient) => {
      setPortalRequiredPatient(null);
      if (p.hasPortalAccount) {
        // The patient already made a portal account, which owns their
        // Patient row. Phone verification still proves who they are, so
        // sign in to that account rather than sending them to the
        // email/password form.
        setForceNewPatient(false);
        const attempt_1 = flow.actions.startAttempt(AuthMethodId.PHONE, "phone_portal_sign_in");
        portalSignInViaPhoneRef.current = true;
        try {
          await portalSignInMutation.mutateAsync(p.id);
          attempt_1?.finish("access_unconfirmed");
        } catch (error) {
          portalSignInViaPhoneRef.current = false;
          if (error instanceof PortalLoginRequiredError && !flow.meta.allowPortalAccountHolders) {
            // A portal account with staff access is never opened by a
            // phone code; fall back to the email/password handoff.
            attempt_1?.finish("portal_handoff");
            setPortalRequiredPatient(p);
            posthog.capture("patient_portal_login_required", {
              surface: flow.meta.surface,
              source: "patient_picker"
            });
            return;
          }
          attempt_1?.fail(error);
          if (error instanceof PhoneCodeExpiredError) {
            // The picker can't send a new code; code entry can.
            setEnteredCode("");
          }
          if (error instanceof PortalLoginRequiredError || error instanceof PhoneCodeExpiredError) {
            flow.actions.setError(AuthMethodId.PHONE, error.message);
          }
        }
        return;
      }
      setSelectedPatient(p);
      setForceNewPatient(false);
      if (!hasFullName(p)) return;
      if (claimedPatient?.id === p.id) return;
      const attempt_2 = flow.actions.startAttempt(AuthMethodId.PHONE, "phone_claim");
      // Wait for the claim to succeed before bubbling success — the
      // `claimedPatient` effect fires onSuccess once the cache update
      // lands. Showing "Linking…" for the claim RTT is preferable to
      // optimistically advancing the parent flow and leaving the
      // server with no claim if the request fails.
      try {
        const claimed = await claimMutation.mutateAsync(p.id);
        attempt_2?.finish(claimed ? "access_unconfirmed" : "backend_error");
      } catch (error_0) {
        attempt_2?.fail(error_0);
        setSelectedPatient(null);
      }
    },
    completeName: async (firstName: string, lastName: string) => {
      const attempt_3 = selectedPatient ? flow.actions.startAttempt(AuthMethodId.PHONE, "phone_claim") : null;
      try {
        if (selectedPatient) {
          // Claim before updating — PUT /auth/patient requires the claim
          // to be on the token.
          const claimed_0 = await claimMutation.mutateAsync(selectedPatient.id);
          if (!claimed_0) {
            toast({
              title: "Error",
              description: "We couldn't link this patient. Please try again.",
              variant: "error"
            });
            attempt_3?.finish("backend_error");
            return;
          }
          const updated = await updatePatient(claimed_0.id, {
            firstName,
            lastName
          });
          if (updated) {
            // Write the updated patient (with the new name) back into the
            // query cache so the `claimedPatient` effect re-fires with
            // the fresh shape instead of the pre-name claim row.
            updateClaimedPatient(updated);
            setSelectedPatient(updated);
            attempt_3?.finish("access_unconfirmed");
            return;
          }
          toast({
            title: "Error",
            description: "We were unable to update your patient information. Please try again.",
            variant: "error"
          });
          attempt_3?.finish("backend_error");
          return;
        }
        const patientSource = flow.meta.surface === AuthSurface.SELF_SCHEDULE ? PatientSource.SELF_SCHEDULING : undefined;
        const created = await createAndClaimMutation.mutateAsync({
          phoneNumber,
          firstName,
          lastName,
          firebaseUid: "",
          patientSource,
          primaryLocationId: signUpLocationId(patientSource)
        });
        if (created) {
          setSelectedPatient(created);
          return;
        }
        toast({
          title: "Error",
          description: "We were unable to create a new patient. Please try again.",
          variant: "error"
        });
      } catch (err) {
        attempt_3?.fail(err);
        flow.actions.setError(AuthMethodId.PHONE, err instanceof Error ? err.message : "Could not save your name.");
      }
    },
    requirePortalLogin: (patient: SelfSchedulePatient) => {
      flow.actions.startAttempt(AuthMethodId.PHONE, "phone_claim")?.finish("portal_handoff");
      setPortalRequiredPatient(patient);
      setForceNewPatient(false);
      setSelectedPatient(null);
      posthog.capture("patient_portal_login_required", {
        surface: flow.meta.surface,
        source: "patient_picker"
      });
    },
    resend: async () => {
      if (!phoneNumber) return;
      setPortalRequiredPatient(null);
      const attempt_4 = flow.actions.startAttempt(AuthMethodId.PHONE, "phone_code_send", true);
      const result = await flow.actions.runAuth(AuthMethodId.PHONE, () => sendMutation.mutateAsync(phoneNumber), attempt_4);
      if (result !== null) attempt_4?.finish("code_sent");
    },
    startNewPatient: () => {
      setPortalRequiredPatient(null);
      setForceNewPatient(true);
      setSelectedPatient(null);
    }
  }), [flow.actions, flow.meta, sendMutation, verifyMutation, claimMutation, createAndClaimMutation, portalSignInMutation, claimedPatient, selectedPatient, phoneNumber, updateClaimedPatient]);
  const state: PhoneAuthState = {
    step,
    phoneNumber,
    enteredCode,
    candidates,
    selectedPatient,
    portalRequiredPatient,
    // Combine the global "Firebase replay in progress" signal with the
    // device-local "we already picked someone, server is catching up"
    // signal so PhoneInner shows the "Welcome back" placeholder during
    // either, without ever ripping out an in-flight phone form.
    isResolvingSession: isResolving || isResolvingClaim,
    isSendingCode: sendMutation.isPending,
    isVerifyingCode: verifyMutation.isPending,
    isClaiming: claimMutation.isPending || createAndClaimMutation.isPending || portalSignInMutation.isPending,
    // `isPending` so cached candidates short-circuit the skeleton; only
    // the genuine no-data-yet state shows loading.
    isLoadingCandidates: isCandidatesPending && !verifyMutation.data && candidates.length === 0
  };
  return {
    state,
    actions
  };
}